import { Box, IconButton, InputAdornment, SxProps, Theme } from '@mui/material';
import {
  AlertCircle as AlertCircleIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Eye,
  EyeOff
} from '@opengov/react-capital-assets';
import React, { MouseEventHandler } from 'react';

/**
 *
 * Properly types a styles object filled will SxProps objects
 * @param styles object with nested SxProps objects
 * @returns styles properly typed
 */
export function createTypedSxStyles<T extends Record<string, SxProps<Theme>>>(styles: T) {
  return styles;
}

export const circularProgressSizeProps = {
  tiny: {
    size: 12,
    thickness: 7
  },
  extraXSmall: {
    size: 13,
    thickness: 7
  },
  extraSmall: {
    size: 16,
    thickness: 7
  },
  small: {
    size: 20,
    thickness: 7
  },
  medium: {
    size: 40,
    thickness: 5
  },
  large: {
    size: 100,
    thickness: 3
  }
};

export function getTextFieldIconByStatus(status: string) {
  switch (status) {
    // These icons are plain SVG components (not MUI SvgIcon) with a fixed 16px size, so MUI icon props such as
    // htmlColor and fontSize have no effect. Box + sx applies the palette colour as a class, which follows dark mode.
    // All of them are decorative (the field's state and helper text carry the status; the IconButton's aria-label names
    // the action), so hide them from assistive tech the way MUI SvgIcon does by default.
    case 'error':
      return <Box component={AlertCircleIcon} sx={{ color: 'error.main' }} aria-hidden="true" focusable="false" />;
    case 'success':
      return <Box component={CheckCircleIcon} sx={{ color: 'success.main' }} aria-hidden="true" focusable="false" />;
    case 'clear':
      return <CloseIcon aria-hidden="true" focusable="false" />;
    case 'visible':
      return <Eye aria-hidden="true" focusable="false" />;
    case 'hidden':
      return <EyeOff aria-hidden="true" focusable="false" />;
    default:
      return null;
  }
}

// Default accessible names for the adornments that act as buttons. `visible` / `hidden` are one show-password toggle, so
// the name stays the same and aria-pressed carries the state.
const ADORNMENT_BUTTON_LABELS: Record<string, string> = {
  clear: 'Clear',
  visible: 'Show password',
  hidden: 'Show password'
};

/**
 * Returns the end adornment for a TextField status: the icon, or an IconButton around it when `onClick` is given.
 * @param ariaLabel Accessible name for the button. Defaults to "Clear" / "Show password" for those statuses; pass it to
 * localise, or for any other status (its icon is decorative, so without a label the button has no name).
 */
export function getTextFieldEndAdornment(status: string, onClick?: MouseEventHandler | undefined, ariaLabel?: string) {
  if (!status) return null;
  const icon = getTextFieldIconByStatus(status);
  const isPasswordToggle = status === 'visible' || status === 'hidden';
  // Without an icon there is nothing to click, and an empty button would still take focus.
  const child =
    onClick && icon ? (
      <IconButton
        onClick={onClick}
        aria-label={ariaLabel ?? ADORNMENT_BUTTON_LABELS[status]}
        aria-pressed={isPasswordToggle ? status === 'visible' : undefined}
      >
        {icon}
      </IconButton>
    ) : (
      icon
    );
  return <InputAdornment position="end">{child}</InputAdornment>;
}

export const preventPasteNonNumericCharacters = (event: React.ClipboardEvent<HTMLInputElement>) => {
  const pasteText = event.clipboardData?.getData('text/plain');
  if (!pasteText) return;
  const formattedPasteText = pasteText.replace(/[^\d.-]/g, '');
  if (formattedPasteText === pasteText) return;
  event.preventDefault();
  const { target } = event;
  const { selectionStart = null, selectionEnd = null, value = '' } = target as HTMLInputElement;
  if (selectionStart === null || selectionEnd === null) {
    (target as HTMLInputElement).value = formattedPasteText;
  } else {
    const textBefore = value.substring(0, selectionStart);
    const textAfter = value.substring(selectionEnd, value.length);
    (target as HTMLInputElement).value = `${textBefore}${formattedPasteText}${textAfter}`;
  }
};

export const preventEnterNonNumericCharacters = (event: React.KeyboardEvent<HTMLInputElement>) => {
  // Allow keyboard shortcuts
  const { key } = event;
  if (
    event.getModifierState &&
    (event.getModifierState('Meta') || event.getModifierState('Control') || event.getModifierState('Alt'))
  ) {
    return;
  }
  // Allow non-printable keys
  if (key.length !== 1 || key === '\x00') {
    return;
  }
  // Prevent any non-numeric keys, but allow "." for decimals and "-" for negative values
  if ((key < '0' || key > '9') && key !== '.' && key !== '-') {
    event.preventDefault();
  }
};
